'use client';

import {
  type ColorScheme,
  NavigationBarType,
  NavigationPosition,
} from '@unifyapps/defs/types/interface';
import { useGetQueryMeta } from '@unifyapps/network/hooks/useGetQueryMeta';
import { Box } from '@unifyapps/ui/components/Box';
import { slowCn } from '@unifyapps/ui/lib/utils';
import { ThemeOverrideContextProvider } from '@unifyapps/ui/providers/ThemeOverrideContext';
import type { RefObject } from 'react';
import { forwardRef, memo, useMemo } from 'react';
import { EMPTY_OBJECT } from '../../consts/empty';
import { useParams, usePathname } from '../../navigation';
import { DEFAULT_NAVIGATION_ITEM_APPEARANCE } from './constants';
import { isPlatformSideNav } from './helpers';
import type {
  NavigationClassNamesType,
  NavigationPropsType,
  NavigationStylesType,
  SideNavProps,
} from './types';
import { getPreviewNavItems } from './utils';
import { BottomNavigation } from './variants/BottomNavigation/BottomNavigation';
import { useBottomNavItems } from './variants/BottomNavigation/hooks/useBottomNavItems';
import type { UseAppSideNavStateProps } from './variants/SideNavigation/hooks/useAppSideNavState';
import { SideNavigation } from './variants/SideNavigation/SideNavigation';

type NavigationContainerProps = {
  navigationProps: NavigationPropsType;
  navigationStyles?: NavigationStylesType;
  navigationClassNames: NavigationClassNamesType;
  sideNavRef?: RefObject<HTMLDivElement | null>;
  navigationContainerRef?: RefObject<HTMLDivElement | null>;
  disableHoverListeners?: boolean;
  controlledState?: UseAppSideNavStateProps['controlledState'];
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  className?: string;
  scheme?: 'light' | 'dark';
} & Pick<SideNavProps, 'slots'>;

const NavigationContainer = forwardRef<
  HTMLDivElement,
  NavigationContainerProps & {
    pathname: string;
    projectSlug: string | undefined;
  }
>((props, ref) => {
  const {
    navigationClassNames,
    navigationStyles,
    navigationProps,
    sideNavRef,
    navigationContainerRef,
    pathname,
    projectSlug,
    className,
    ...rest
  } = props;

  const {
    position,
    navigationItemAppearance = DEFAULT_NAVIGATION_ITEM_APPEARANCE,
    floatingOffset,
    navigation,
    disableNavItemClick = false,
    theme,
  } = navigationProps;

  const { containerClassName } = navigationClassNames;
  const { containerStyles } = navigationStyles ?? (EMPTY_OBJECT as NavigationStylesType);

  const navigationBarType = navigationProps.navigationBarType as unknown as NavigationBarType;
  const floatingClassName = navigationBarType === NavigationBarType.Floating ? floatingOffset : '';

  const adaptedNavigationProps = useMemo(() => {
    return {
      ...navigationProps,
      navigation: getPreviewNavItems(navigation, disableNavItemClick),
      navigationItemAppearance,
    };
  }, [disableNavItemClick, navigation, navigationItemAppearance, navigationProps]);

  const isBottomNav = position === NavigationPosition.Bottom;

  const { hasActiveNavItem: hasActiveBottomNavItem } = useBottomNavItems({
    navigation: adaptedNavigationProps.navigation,
    pathname,
    projectSlug,
  });

  if (isBottomNav && !hasActiveBottomNavItem) {
    return null;
  }

  const navigationContent = (
    <Box
      className={slowCn(className, floatingClassName, containerClassName)}
      data-color-scheme={theme}
      ref={navigationContainerRef}
      style={containerStyles}
    >
      {isBottomNav ? (
        <BottomNavigation
          navigationClassNames={navigationClassNames}
          navigationProps={adaptedNavigationProps}
          navigationStyles={navigationStyles}
          pathname={pathname}
          projectSlug={projectSlug}
          ref={ref}
          {...rest}
        />
      ) : (
        <SideNavigation
          navigationClassNames={navigationClassNames}
          navigationProps={adaptedNavigationProps}
          navigationStyles={navigationStyles}
          pathname={pathname}
          projectSlug={projectSlug}
          ref={ref}
          scheme={theme as 'light' | 'dark' | undefined}
          sideNavRef={sideNavRef}
          {...rest}
        />
      )}
    </Box>
  );

  // platform sidenav is chrome, not an interface surface: it must NOT establish an
  // interface-theme scope (its portalled popovers would otherwise inherit an interface
  // preview's `.interface-theme` tokens and bleed). it draws platform tokens from the
  // ambient `is-platform-shell` shell instead, so we skip the provider entirely for it.
  if (isPlatformSideNav(navigationItemAppearance.labelVariant)) {
    return navigationContent;
  }

  return (
    <ThemeOverrideContextProvider scheme={theme as ColorScheme | undefined} style="">
      {navigationContent}
    </ThemeOverrideContextProvider>
  );
});

NavigationContainer.displayName = 'NavigationContainer';

const NavigationContainerMemo = memo(NavigationContainer);

const NavigationContainerWrapper = forwardRef<HTMLDivElement, NavigationContainerProps>(
  (props, ref) => {
    const queryMeta = useGetQueryMeta();
    const basePath = queryMeta?.basePath;
    // remove basePath from pathname to since all nav items are defined without basePath to correctly match the active nav item
    const pathname = usePathname().replace(basePath ?? '', '');
    const { projectSlug } = useParams<{ projectSlug?: string }>();

    // Pass pathname and projectSlug as props to NavigationContainerMemo so that NavigationContainer doesn't get re-render on unrelated route changes
    return (
      <NavigationContainerMemo pathname={pathname} projectSlug={projectSlug} ref={ref} {...props} />
    );
  },
);

NavigationContainerWrapper.displayName = 'NavigationContainerWrapper';

export { NavigationContainerWrapper as NavigationContainer };
